import {
  ActionList,
  Button,
  Checkbox,
  HorizontalGrid,
  Modal,
  Popover,
  Select,
  Text,
  TextField,
  VerticalStack,
} from '@shopify/polaris';
import { useContext, useEffect, useState } from 'react';
import ToastContext from '../../../../context/ToastContext';
import { SET_ERROR, SET_OK } from '../../../../context/ToastReducer';
import UserContext from '../../../../context/UserContext';
import { NEW_FILE_FAILED, NEW_FILE_LOADED, NEW_FILE_LOADING } from '../../../../context/UserReducer';
import {
  getReportsStocksExcel,
  getReportsStocksFullPdf,
  getReportsStocksViewPdf,
} from '../../../../services/reports/stocksService';
import { triggerBlobDownload } from '../efficacy/downloads';
import { MEASURES, MEASURE_BY_KEY, MONEY_SCALES } from './constants';

const MODES = {
  view: {
    menu: 'PDF vista actual',
    title: 'Exportar PDF de la vista actual',
    slug: 'vista',
    ext: 'pdf',
  },
  full: {
    menu: 'PDF informe completo',
    title: 'Exportar PDF informe completo',
    slug: 'completo',
    ext: 'pdf',
  },
  excel: {
    menu: 'Excel',
    title: 'Exportar Excel',
    slug: 'datos',
    ext: 'xlsx',
  },
};

const CHAPTERS = [
  { key: 'includeCover', label: 'Portada e índice' },
  { key: 'includeResumen', label: 'Resumen ejecutivo' },
  { key: 'includeDepositos', label: 'Por depósito' },
  { key: 'includeCategorias', label: 'Por género y categoría' },
  { key: 'includeCampanas', label: 'Por campaña' },
  { key: 'includeAntiguedad', label: 'Antigüedad' },
  {
    key: 'includeComparativa',
    label: 'Comparativa',
    needsCompare: true,
  },
];

const allChaptersOn = () => CHAPTERS.reduce((acc, c) => ({ ...acc, [c.key]: true }), {});

// Con `responseType: 'blob'` el cuerpo de un error también llega como Blob.
const errorMessage = async (e) => {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text())?.message;
    } catch (_) {
      return null;
    }
  }
  return data?.message;
};

/**
 * Modal de exportación. `base` lleva ya los params de la consulta (fechas,
 * ámbito, filtros, pautado y moneda); aquí se añaden título, subtítulo,
 * nombre de fichero y las opciones de cada modo. Se genera en el servidor y
 * se registra en la campanita de descargas, como en Eficacia.
 */
const StocksExportModal = ({
  mode,
  onClose,
  base,
  defaults,
  hasCompare,
  moneyScale,
  measures,
  reprOn,
  reprMeasure,
}) => {
  const [, dispatchUser] = useContext(UserContext);
  const [, dispatchToast] = useContext(ToastContext);
  const def = MODES[mode];

  const [filename, setFilename] = useState('');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [scale, setScale] = useState(moneyScale);
  const [includeCover, setIncludeCover] = useState(true);
  const [includeCharts, setIncludeCharts] = useState(true);
  const [chapters, setChapters] = useState(allChaptersOn);
  // PDF vista actual: medidas y representación, por defecto como en pantalla.
  const [pdfMeasures, setPdfMeasures] = useState(measures);
  const [pdfRepr, setPdfRepr] = useState(reprOn);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!mode) return;
    setFilename(`${defaults.fileStem.replace('{slug}', MODES[mode].slug)}`);
    setTitle(mode === 'view' ? defaults.viewTitle : defaults.title);
    setSubtitle(defaults.subtitle);
    setScale(moneyScale);
    setIncludeCover(true);
    setIncludeCharts(true);
    setChapters(allChaptersOn());
    setPdfMeasures(measures);
    setPdfRepr(reprOn);
  }, [mode, defaults, moneyScale, measures, reprOn]);

  // Siempre queda una medida; la representada no se puede quitar sin
  // apagar antes la representación, y encenderla trae su medida.
  const toggleMeasure = (key, on) =>
    setPdfMeasures((prev) => {
      if (on) return MEASURES.map((m) => m.key).filter((k) => k === key || prev.includes(k));
      const next = prev.filter((k) => k !== key);
      return next.length ? next : prev;
    });
  const toggleRepr = (on) => {
    setPdfRepr(on);
    if (on) toggleMeasure(reprMeasure, true);
  };

  if (!mode) return null;

  const handleExport = async () => {
    setBusy(true);
    const fileId = `${filename}-${Date.now()}`;
    dispatchUser({
      type: NEW_FILE_LOADING,
      payload: { id: fileId, type: def.menu, name: title || filename, date: new Date() },
    });
    dispatchToast({ type: SET_OK, payload: { message: 'Generando export, puede tardar' } });
    onClose();
    try {
      const common = { ...base, title, subtitle, filename };
      let resp;
      if (mode === 'view') {
        resp = await getReportsStocksViewPdf({
          ...common,
          measures: pdfMeasures.join(':'),
          ...(pdfRepr ? { repr: reprMeasure } : {}),
          moneyScale: scale,
          includeCover: String(includeCover),
          includeCharts: String(includeCharts),
        });
      } else if (mode === 'full') {
        const flags = CHAPTERS.reduce(
          (acc, c) => ({ ...acc, [c.key]: String(Boolean(chapters[c.key]) && (!c.needsCompare || hasCompare)) }),
          {},
        );
        resp = await getReportsStocksFullPdf({ ...common, moneyScale: scale, ...flags });
      } else {
        resp = await getReportsStocksExcel(common);
      }
      const finalName = filename.endsWith(`.${def.ext}`) ? filename : `${filename}.${def.ext}`;
      const blobUrl = URL.createObjectURL(resp.data);
      triggerBlobDownload(resp.data, finalName);
      // Como en Eficacia: el PDF además se abre en una pestaña nueva (el
      // navegador puede bloquearlo si la generación tarda; la descarga ya
      // está hecha). El Excel solo se descarga.
      if (def.ext === 'pdf') window.open(blobUrl, '_blank', 'noopener');
      dispatchUser({ type: NEW_FILE_LOADED, payload: { id: fileId, url: blobUrl } });
      dispatchToast({ type: SET_OK, payload: { message: 'Export generado' } });
    } catch (e) {
      dispatchUser({ type: NEW_FILE_FAILED, payload: { id: fileId } });
      dispatchToast({ type: SET_ERROR, payload: { message: (await errorMessage(e)) || 'Error generando el export' } });
    } finally {
      setBusy(false);
    }
  };

  const scaleSelect = (
    <Select
      label="Importes"
      options={MONEY_SCALES.map((s) => ({ label: s.label, value: s.key }))}
      value={scale}
      onChange={setScale}
    />
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={def.title}
      primaryAction={{ content: 'Exportar', onAction: handleExport, loading: busy, disabled: !filename.trim() }}
      secondaryActions={[{ content: 'Cancelar', onAction: onClose, disabled: busy }]}
    >
      <Modal.Section>
        <VerticalStack gap="3">
          <TextField label="Nombre del fichero" value={filename} onChange={setFilename} autoComplete="off" />
          <TextField label="Título" value={title} onChange={setTitle} autoComplete="off" />
          <TextField label="Subtítulo" value={subtitle} onChange={setSubtitle} autoComplete="off" />
        </VerticalStack>
      </Modal.Section>

      {mode === 'view' && (
        <Modal.Section>
          <VerticalStack gap="2">
            <Text variant="headingSm" as="h3">
              Incluir
            </Text>
            <Checkbox label="Portada e índice" checked={includeCover} onChange={setIncludeCover} />
            <Checkbox label="Gráficas" checked={includeCharts} onChange={setIncludeCharts} />
            {scaleSelect}
          </VerticalStack>
        </Modal.Section>
      )}

      {mode === 'view' && (
        <Modal.Section>
          <VerticalStack gap="2">
            <Text variant="headingSm" as="h3">
              Medidas
            </Text>
            <HorizontalGrid columns={2} gap="2">
              {MEASURES.map((m) => {
                const checked = pdfMeasures.includes(m.key);
                const locked = (checked && pdfMeasures.length === 1) || (pdfRepr && m.key === reprMeasure);
                return (
                  <Checkbox
                    key={m.key}
                    label={m.label}
                    checked={checked}
                    disabled={locked}
                    onChange={(v) => toggleMeasure(m.key, v)}
                  />
                );
              })}
              <Checkbox
                label={`Representación (% ${MEASURE_BY_KEY[reprMeasure]?.label || ''})`}
                checked={pdfRepr}
                onChange={toggleRepr}
              />
            </HorizontalGrid>
          </VerticalStack>
        </Modal.Section>
      )}

      {mode === 'full' && (
        <Modal.Section>
          <VerticalStack gap="2">
            <Text variant="headingSm" as="h3">
              Capítulos
            </Text>
            {CHAPTERS.map((c) => (
              <Checkbox
                key={c.key}
                label={c.label}
                disabled={c.needsCompare && !hasCompare}
                checked={Boolean(chapters[c.key]) && (!c.needsCompare || hasCompare)}
                onChange={(v) => setChapters((prev) => ({ ...prev, [c.key]: v }))}
              />
            ))}
            {scaleSelect}
          </VerticalStack>
        </Modal.Section>
      )}
    </Modal>
  );
};

export const StocksExportMenu = ({
  disabled,
  base,
  defaults,
  hasCompare,
  moneyScale,
  measures,
  reprOn,
  reprMeasure,
}) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null);
  return (
    <>
      <Popover
        active={open}
        activator={
          <Button onClick={() => setOpen((v) => !v)} disclosure disabled={disabled}>
            Exportar
          </Button>
        }
        onClose={() => setOpen(false)}
        preferredAlignment="right"
      >
        <ActionList
          items={Object.entries(MODES).map(([k, m]) => ({
            content: m.menu,
            onAction: () => {
              setOpen(false);
              setMode(k);
            },
          }))}
        />
      </Popover>
      <StocksExportModal
        mode={mode}
        onClose={() => setMode(null)}
        base={base}
        defaults={defaults}
        hasCompare={hasCompare}
        moneyScale={moneyScale}
        measures={measures}
        reprOn={reprOn}
        reprMeasure={reprMeasure}
      />
    </>
  );
};
